/**
 * Cubera partner pixel (anl-edge.cubera.co) — separate from CRM /api/visits/track-visit.
 *
 * Landing URLs may include e.g. ?click_id=...&campaign=... (macros expand on click).
 * All query params from that capture are stored and forwarded to POST /api/dsp-tracker
 * as attributionParams (plus clickId / campaign for compatibility).
 *
 * Optional: REACT_APP_CUBERA_TOKEN — appended to Cubera edge URL only if set (invalid token breaks Cubera only).
 * CRM POST /api/dsp-tracker runs whenever click_id or campaign exist in storage — independent of Cubera success.
 *
 * Events: 0 = book page (ad landing), 1 = Pay Now click, 2 = thank-you after payment (+ price).
 */

const STORAGE_KEY = 'daiv_cubera_attribution';
const CUBERA_BASE = 'https://anl-edge.cubera.co/tracking/action.json';
const DSP_TRACKER_API = 'https://crm.daiv.co.in/api/dsp-tracker';
const FLAG_E0_PREFIX = 'daiv_cubera_sent_e0_';
const FLAG_E1_PREFIX = 'daiv_cubera_sent_e1_';
const FLAG_E2_PREFIX = 'daiv_cubera_sent_e2_';
const ALLOW_REPEAT_IN_DEV = process.env.REACT_APP_CUBERA_ALLOW_REPEAT_IN_DEV === 'true';

const IS_DEV = process.env.NODE_ENV === 'development';

function dspLog(...args) {
  if (IS_DEV) console.debug('[DSP]', ...args);
}

function shouldDeduplicateInSession() {
  if (process.env.NODE_ENV === 'production') return true;
  return !ALLOW_REPEAT_IN_DEV;
}

function resolveCampaign(params, storedCampaign = '') {
  if (storedCampaign) return storedCampaign;
  if (!params || typeof params !== 'object') return '';
  return params.campaign || params.utm_campaign || params.campaign_id || '';
}

function hasAttribution() {
  const a = getCuberaAttribution();
  return !!(a.click_id || a.campaign);
}

/**
 * Capture query params from the URL (e.g. click_id, campaign, and any other keys the ad platform adds).
 * Persists click_id/campaign for the Cubera pixel plus a full key/value map for the DSP API.
 */
export function captureCuberaParamsFromUrl(search = '') {
  if (typeof window === 'undefined') return;
  const query = search || window.location?.search || '';
  if (!query) return;
  const params = new URLSearchParams(query);
  const allParams = {};
  params.forEach((value, key) => {
    if (value !== '') allParams[key] = value;
  });
  // Cubera templates often use utm_click / utm_id (not click_id).
  const click_id =
    allParams.click_id || allParams.utm_click || allParams.utm_id || allParams.CLICK_ID || '';
  const campaign = resolveCampaign(allParams);
  if (!click_id && !campaign) return;
  try {
    const next = {
      click_id,
      campaign,
      params: allParams,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    dspLog('Attribution captured', { keys: Object.keys(next.params || {}) });
  } catch {
    // ignore
  }
}

export function getCuberaAttribution() {
  if (typeof window === 'undefined') {
    return { click_id: '', campaign: '', params: {} };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { click_id: '', campaign: '', params: {} };
    const p = JSON.parse(raw);
    const params = typeof p.params === 'object' && p.params !== null && !Array.isArray(p.params) ? p.params : {};
    return {
      click_id: p.click_id || '',
      campaign: resolveCampaign(params, p.campaign || ''),
      params,
    };
  } catch {
    return { click_id: '', campaign: '', params: {} };
  }
}

function normalizeEventStage(event) {
  const n = Number(event);
  if (n === 0 || n === 1 || n === 2) return n;
  return 0;
}

function postDspTracker({ event, price, clickId, campaign, attributionParams }) {
  const stage = normalizeEventStage(event);
  const payload = {
    event: stage,
    price: Number.isFinite(price) ? price : 0,
    clickId: clickId || '',
    campaign: campaign || '',
    attributionParams: attributionParams && typeof attributionParams === 'object' ? attributionParams : {},
  };

  fetch(DSP_TRACKER_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  })
    .then((res) => {
      if (!res.ok) {
        console.warn('[DSP Tracker] Request returned status', res.status);
      }
    })
    .catch((err) => {
      console.warn('[DSP Tracker] Request failed:', err?.message || err);
    });
}

function tryFallbackCuberaFetch(url) {
  if (typeof fetch !== 'function') return;
  fetch(url, {
    method: 'GET',
    mode: 'no-cors',
    keepalive: true,
    cache: 'no-store',
  }).catch(() => {});
}

/**
 * Fire Cubera edge pixel (GET via Image) and always mirror the same event to CRM DSP.
 * event: 0 | 1 | 2, price in INR (decimal).
 * DSP runs first — Cubera 400/blockers do not affect the CRM record.
 */
export function fireCuberaPixel({ event, price = 0 }) {
  if (!hasAttribution()) {
    dspLog(`Event ${event} skipped: no click_id or campaign in storage`);
    return;
  }

  const stage = normalizeEventStage(event);
  const attr = getCuberaAttribution();
  const priceNum = Number(price);

  postDspTracker({
    event: stage,
    price: Number.isFinite(priceNum) ? priceNum : 0,
    clickId: attr.click_id || '',
    campaign: attr.campaign || '',
    attributionParams: attr.params || {},
  });

  try {
    const u = new URL(CUBERA_BASE);
    if (attr.click_id) u.searchParams.set('token', attr.click_id);
    u.searchParams.set('event', String(stage));
    if (Number.isFinite(priceNum) && priceNum > 0) u.searchParams.set('price', priceNum.toFixed(2));
    u.searchParams.set('_t', String(Date.now()));

    dspLog('Cubera pixel request', { event: stage, price: priceNum });

    const img = new Image();

    img.onload = () => {
      dspLog('Cubera pixel load completed', { event: stage });
    };

    img.onerror = () => {
      tryFallbackCuberaFetch(u.toString());
    };

    img.referrerPolicy = 'no-referrer-when-downgrade';
    img.src = u.toString();
  } catch (err) {
    if (IS_DEV) {
      console.warn('[Cubera] Pixel setup failed:', err?.message || err);
    }
  }
}

/** event=0: book page ad landing — once per slug per browser tab session when partner params exist */
export function tryFireCuberaPoojaPageView(poojaSlug) {
  if (!hasAttribution() || !poojaSlug) {
    dspLog('Event 0 skipped: no attribution or slug');
    return;
  }
  if (!shouldDeduplicateInSession()) {
    fireCuberaPixel({ event: 0, price: 0 });
    return;
  }
  const key = `${FLAG_E0_PREFIX}${poojaSlug}`;
  try {
    if (sessionStorage.getItem(key)) {
      dspLog('Event 0 skipped: already sent for this pooja in session');
      return;
    }
    sessionStorage.setItem(key, '1');
  } catch {
    // ignore
  }
  fireCuberaPixel({ event: 0, price: 0 });
}

/** event=1: Pay Now click (booking validated) — once per slug in this tab */
export function tryFireCuberaBookingStepOneComplete(poojaSlug) {
  if (!hasAttribution() || !poojaSlug) {
    dspLog('Event 1 skipped: missing attribution or slug');
    return;
  }
  if (!shouldDeduplicateInSession()) {
    fireCuberaPixel({ event: 1, price: 0 });
    return;
  }
  const key = `${FLAG_E1_PREFIX}${poojaSlug}`;
  try {
    if (sessionStorage.getItem(key)) {
      dspLog('Event 1 skipped: already sent for this pooja in session');
      return;
    }
    sessionStorage.setItem(key, '1');
  } catch {
    // ignore
  }
  fireCuberaPixel({ event: 1, price: 0 });
}

/** event=2: thank-you page after payment — amount in INR, once per pooja slug per tab */
export function fireCuberaPaymentSuccess(amountInr, poojaSlug) {
  if (!hasAttribution()) {
    dspLog('Event 2 skipped: no attribution');
    return;
  }
  if (poojaSlug && shouldDeduplicateInSession()) {
    const key = `${FLAG_E2_PREFIX}${poojaSlug}`;
    try {
      if (sessionStorage.getItem(key)) {
        dspLog('Event 2 skipped: already sent for this pooja in session');
        return;
      }
      sessionStorage.setItem(key, '1');
    } catch {
      // ignore
    }
  }
  const p = Number(amountInr);
  fireCuberaPixel({ event: 2, price: Number.isFinite(p) ? p : 0 });
}
